import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAnnouncements } from "@/context/AnnouncementsContext";
import { AnnouncementBody, PriorityBadge, formatStamp } from "@/components/AnnouncementBody";
import { AnnouncementFullscreen } from "@/components/AnnouncementFullscreen";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { LockKey, Megaphone, CheckCircle, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const StateChips = ({ a }) => (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`notice-chips-${a.id}`}>
        {a.presented_at && (
            <Badge className="bg-slate-100 text-slate-700 border border-slate-200 font-normal">
                <Eye size={12} className="mr-1" /> Seen {formatStamp(a.presented_at)}
            </Badge>
        )}
        {a.unlocked_at && (
            <Badge className="bg-blue-50 text-blue-800 border border-blue-200 font-normal">
                <LockKey size={12} className="mr-1" /> Unlocked {formatStamp(a.unlocked_at)}
            </Badge>
        )}
        {a.acknowledged_at && (
            <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 font-normal">
                <CheckCircle size={12} className="mr-1" /> Acknowledged {formatStamp(a.acknowledged_at)}
            </Badge>
        )}
        {a.requires_acknowledgment && !a.acknowledged_at && (
            <Badge className="bg-amber-100 text-amber-900 border border-amber-200 font-normal">
                Acknowledgment required
            </Badge>
        )}
    </div>
);

export default function Notices() {
    const { announcements, loading, patch } = useAnnouncements();
    const [openId, setOpenId] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);

    const active = announcements.find((a) => a.id === openId) || null;

    const open = (id) => {
        setConfirmed(false);
        setOpenId(id);
        const a = announcements.find((x) => x.id === id);
        if (a && !a.presented_at) {
            api.post(`/announcements/${id}/presented`)
                .then(({ data }) => patch(id, { presented_at: data?.presented_at }))
                .catch(() => {});
        }
    };

    const acknowledge = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/announcements/${active.id}/acknowledge`);
            patch(active.id, { acknowledged_at: data?.acknowledged_at });
            toast.success("Acknowledged. Thank you.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not acknowledge");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="notices-page">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">Communication</div>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                    Notices
                </h1>
                <p className="text-slate-600 mt-2 text-sm">
                    Announcements sent to you by the office. Private notices stay locked until you enter your PIN —
                    you never see notices addressed to anyone else.
                </p>
            </div>

            {loading ? (
                <div className="space-y-3">
                    {[1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-md" />)}
                </div>
            ) : announcements.length === 0 ? (
                <Card className="border-[#E2E8F0]" data-testid="notices-empty">
                    <CardContent className="p-10 text-center">
                        <Megaphone size={28} weight="duotone" className="text-slate-300 mx-auto mb-3" />
                        <div className="text-sm text-slate-500">You have no active notices right now.</div>
                        <Link to="/profile" className="text-xs text-[#0066D9] underline mt-2 inline-block">
                            Set your Announcement PIN in My Profile
                        </Link>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {announcements.map((a) => (
                        <Card key={a.id} className="border-[#E2E8F0] an-card-hover" data-testid={`notice-row-${a.id}`}>
                            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-start gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <PriorityBadge priority={a.priority} />
                                        {a.locked && (
                                            <Badge className="bg-slate-800 text-white border border-slate-800 font-normal">
                                                <LockKey size={12} className="mr-1" /> Locked
                                            </Badge>
                                        )}
                                        {!a.presented_at && (
                                            <Badge className="bg-[#0066D9] text-white border-0 font-normal">New</Badge>
                                        )}
                                    </div>
                                    <div className="mt-2 text-sm font-semibold text-[#1F2A44] break-words"
                                        data-testid={`notice-title-${a.id}`}>
                                        {a.locked ? "Private notice — enter your PIN to read" : a.title}
                                    </div>
                                    {!a.locked && (
                                        <div className="text-xs text-slate-500 mt-1 line-clamp-2 break-words">
                                            {a.message || (a.media?.type === "video"
                                                ? "Video notice — open to watch"
                                                : a.media ? "Photo notice — open to view" : "")}
                                        </div>
                                    )}
                                    <StateChips a={a} />
                                </div>
                                <Button variant="outline" onClick={() => open(a.id)}
                                    className="border-[#003366] text-[#003366] hover:bg-slate-50 shrink-0"
                                    data-testid={`notice-open-${a.id}`}>
                                    {a.locked ? "Unlock" : "Read"}
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {active && !active.locked && active.media && (
                <AnnouncementFullscreen
                    announcement={active}
                    mustAcknowledge={active.requires_acknowledgment && !active.acknowledged_at}
                    confirmed={confirmed}
                    onConfirm={setConfirmed}
                    onAcknowledge={acknowledge}
                    busy={busy}
                    onClose={() => setOpenId(null)}
                />
            )}

            <Dialog open={Boolean(active) && !(active && !active.locked && active.media)}
                onOpenChange={(o) => !o && setOpenId(null)}>
                <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="notice-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-base" style={{ fontFamily: "Outfit" }}>
                            {active?.locked ? "Private notice" : "Notice"}
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            {active?.priority === "urgent" ? "Urgent" : "From the office"}
                        </DialogDescription>
                    </DialogHeader>
                    {active && (
                        <>
                            <AnnouncementBody
                                announcement={active}
                                onUnlocked={(data) => patch(active.id, data)}
                            />
                            {active.requires_acknowledgment && !active.acknowledged_at && !active.locked && (
                                <div className="border-t border-[#E2E8F0] pt-4">
                                    <label className="flex items-start gap-2.5 cursor-pointer select-none">
                                        <input type="checkbox" checked={confirmed}
                                            onChange={(e) => setConfirmed(e.target.checked)}
                                            className="mt-0.5 h-4 w-4 accent-[#003366]"
                                            data-testid="notice-confirm-checkbox" />
                                        <span className="text-sm text-[#1F2A44]">
                                            I have read and understood this notice.
                                        </span>
                                    </label>
                                    <Button onClick={acknowledge} disabled={busy || !confirmed}
                                        className="mt-3 bg-[#003366] hover:bg-[#0066D9] h-11"
                                        data-testid="notice-acknowledge-button">
                                        {busy ? "Sending…" : "Acknowledge"}
                                    </Button>
                                </div>
                            )}
                            {active.acknowledged_at && (
                                <div className="text-xs text-emerald-700 border-t border-[#E2E8F0] pt-4"
                                    data-testid="notice-acknowledged-note">
                                    You acknowledged this notice on {formatStamp(active.acknowledged_at)}.
                                </div>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
